import moment from "moment";
import {
  EFormType,
  PublicationChannel,
  PublicationStatus,
  ResultNoticeDuty,
  type PublicationDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { timeLeftUntil, type TimeLeft } from "../../../common/formatting/timeLeft";

const DANGER_DAYS_LEFT = 7;

export type ResultNoticeStep =
  | { kind: "none" }
  | { kind: "askConcludedDate" }
  | { kind: "due"; deadline: string; timeLeft: TimeLeft; urgent: boolean }
  | { kind: "voluntary" }
  | { kind: "sent" }
  | { kind: "published"; externalId: string | null };

export const resultNoticePublication = (event: SourcingEventDto): PublicationDto | undefined => {
  if (event.eventContext.type !== "Public") return undefined;
  return (event.eventContext.publications ?? [])
    .filter(
      (publication) =>
        publication.channel === PublicationChannel.Doffin &&
        (publication.eFormType === EFormType.Can29 || publication.eFormType === EFormType.DesignContestResult36) &&
        publication.status !== PublicationStatus.Failed,
    )
    .at(-1);
};

export const resultNoticeStep = (event: SourcingEventDto, now: number): ResultNoticeStep => {
  const obligation = event.award?.resultNotice;
  if (obligation == null || obligation.duty === ResultNoticeDuty.None) return { kind: "none" };

  const publication = resultNoticePublication(event);
  if (publication?.status === PublicationStatus.Published) {
    return { kind: "published", externalId: publication.externalId ?? null };
  }
  if (publication != null) return { kind: "sent" };
  const recordedOutside = event.award?.externalResultNotice;
  if (recordedOutside != null) return { kind: "published", externalId: recordedOutside.noticeId };

  if (obligation.duty === ResultNoticeDuty.Voluntary) {
    return event.award?.resultNoticeDeclinedAt != null ? { kind: "none" } : { kind: "voluntary" };
  }
  if (obligation.deadline == null) return { kind: "askConcludedDate" };

  const endOfDeadline = moment(obligation.deadline).endOf("day");
  return {
    kind: "due",
    deadline: obligation.deadline,
    timeLeft: timeLeftUntil(endOfDeadline.toISOString(), moment(now)),
    urgent: endOfDeadline.diff(moment(now), "days", true) <= DANGER_DAYS_LEFT,
  };
};
